import { useTranslations } from '@aikoa/i18n/react'
import { usePathname } from 'expo-router'
import React, { useCallback } from 'react'
import { View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { Button } from '~/components/ui/button'
import { Text } from '~/components/ui/text'
import { usePostHog } from '~/lib/analytics/posthog'
import Icon from './Icon'

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
}

interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ComponentType<{ error?: Error; resetError: () => void }>
}

interface ErrorBoundaryInnerProps extends ErrorBoundaryProps {
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

class ErrorBoundaryInner extends React.Component<ErrorBoundaryInnerProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryInnerProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    }
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.props.onError?.(error, errorInfo)
    console.error('Error caught by boundary:', error, errorInfo)
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined })
  }

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback
        return <FallbackComponent error={this.state.error} resetError={this.resetError} />
      }

      return <DefaultErrorFallback error={this.state.error} resetError={this.resetError} />
    }

    return this.props.children
  }
}

export function ErrorBoundary({ children, fallback }: ErrorBoundaryProps) {
  const posthog = usePostHog()
  const pathname = usePathname()

  const handleError = useCallback(
    (error: Error, errorInfo: React.ErrorInfo) => {
      if (!posthog) {
        return
      }

      posthog.capture('error_boundary_caught', {
        route: pathname ?? null,
        error_name: error.name,
        error_message: error.message,
        error_stack: error.stack ? error.stack : null,
        component_stack: errorInfo.componentStack ?? null,
      })
    },
    [pathname, posthog]
  )

  return (
    <ErrorBoundaryInner fallback={fallback} onError={handleError}>
      {children}
    </ErrorBoundaryInner>
  )
}

function DefaultErrorFallback({ error, resetError }: { error?: Error; resetError: () => void }) {
  const { t } = useTranslations('common')

  return (
    <SafeAreaView className="flex-1 items-center justify-center p-4">
      <View className="max-w-sm items-center space-y-4">
        <Icon name="alert-circle" size={48} className="text-destructive" />

        <Text className="text-center font-semibold text-xl">{t(($) => $.error.title)}</Text>

        <Text className="text-center text-foreground">{t(($) => $.error.description)}</Text>

        {__DEV__ && error && (
          <View className="mt-4 rounded-lg bg-secondary p-3">
            <Text className="text-xs text-foreground">{error.message}</Text>
          </View>
        )}

        <Button onPress={resetError} className="mt-4">
          {t(($) => $.actions.tryAgain)}
        </Button>
      </View>
    </SafeAreaView>
  )
}
